import { computed, Injectable, OnDestroy, signal } from "@angular/core";
import { Subscription } from "rxjs";
import { IClippingStudioGfxRepository } from "app/js/src/domain/clipping-studio/i-clipping-studio-gfx-repository";
import { ClippingStudioGraphicEvent } from "app/js/src/domain/clipping-studio/clipping-studio-project";
import { ClippingStudioGfxBridgeService } from "./clipping-studio-gfx-bridge.service";
import { GfxCacheResult } from "app/js/src/domain/clipping-studio/clipping-studio-gfx-models";
import { AspectRatio } from "app/js/src/domain/clipping-studio/aspect-ratio";

/**
 * Runtime entry for a single timeline GFX segment (`ClippingStudioGraphicEvent`).
 *
 * Map key for `activeGfxInstances` is the segment id (`timelineGfxId`), NOT the
 * backend instance id. Each timeline segment gets its own iframe/runtime, even
 * if multiple segments reference the same `gfxInstanceId`.
 */
export interface GfxTimelineRuntime {
  timelineGfxId: string;
  gfxInstanceId: number;
  serveUrl: string;
  status: "materializing" | "ready" | "error";
  params: Record<string, string>;
  x: number;
  y: number;
  scale: number;
}

@Injectable()
export class ClippingStudioGfxManagerService implements OnDestroy {
  /**
   * Active GFX runtimes keyed by timeline segment id (`ClippingStudioGraphicEvent.id`).
   * Every visible GFX segment has exactly one entry here (and exactly one iframe
   * in the bridge under the same key).
   */
  readonly activeGfxInstances = signal<Map<string, GfxTimelineRuntime>>(new Map());
  readonly gfxLoading = signal(false);

  // ── Reactive inputs (set by preview component) ──────────────────────

  /** Current edit-timeline time in ms (same base as overlay visibility filtering). */
  readonly editTimeMs = signal(0);

  /** Effective graphics list (all types). Updated by preview/component on state changes. */
  readonly effectiveGraphics = signal<ClippingStudioGraphicEvent[]>([]);

  // ── Derived state ───────────────────────────────────────────────────

  /** GFX events that are visible at the current editTimeMs and have a loaded, ready iframe. */
  readonly visibleGfxEntries = computed(() => {
    const t = this.editTimeMs();
    const graphics = this.effectiveGraphics();
    const instances = this.activeGfxInstances();
    const loaded = this.gfxBridge.loadedTimelineIds();

    const result: { timelineGfxId: string; localMs: number; graphic: ClippingStudioGraphicEvent }[] = [];
    for (const g of graphics) {
      if (g.type !== "gfx" || g.gfxInstanceId == null) continue;
      if (t < g.inMs || t >= g.outMs) continue;
      const runtime = instances.get(g.id);
      if (!runtime || runtime.status !== "ready") continue;
      if (!loaded.has(g.id)) continue;
      result.push({
        timelineGfxId: g.id,
        localMs: t - g.inMs,
        graphic: g,
      });
    }
    return result;
  });

  private sessionExpiredSub: Subscription | null = null;
  private reopenDebounce = new Map<string, ReturnType<typeof setTimeout>>();
  private container: HTMLElement | null = null;
  // Materialization caches stay instance-keyed: `gfxInstanceId` identifies the
  // backend GFX package/URL, which is shared across all timeline segments that
  // reference the same instance.
  private materializeInFlight = new Map<number, Promise<GfxCacheResult>>();
  private lastMaterializeResult = new Map<number, GfxCacheResult>();

  constructor(
    private readonly gfxRepository: IClippingStudioGfxRepository,
    private readonly gfxBridge: ClippingStudioGfxBridgeService,
  ) {
    this.sessionExpiredSub = this.gfxBridge.sessionExpired$.subscribe((timelineGfxId) =>
      this.handleSessionExpired(timelineGfxId),
    );
  }

  /**
   * Returns a normalized scale coefficient for iframe sizing.
   *
   * Contract: `params["Scale"]` is always a coefficient (1.0 = 100%).
   */
  private getIframeScaleFromParams(params: Record<string, string> | undefined): number {
    const raw = params ? params["Scale"] : undefined;
    const parsed = raw != null ? parseFloat(String(raw)) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  ngOnDestroy(): void {
    this.sessionExpiredSub?.unsubscribe();
    this.sessionExpiredSub = null;
    for (const timer of this.reopenDebounce.values()) {
      clearTimeout(timer);
    }
    this.reopenDebounce.clear();
    this.gfxBridge.destroyAll();
  }

  setContainer(el: HTMLElement | null): void {
    this.container = el;
  }

  /**
   * Single source of truth for base stage sizing for all GFX iframes in reframe flow.
   * Trigger this ONLY when the user changes the aspect ratio option.
   */
  setCurrentAspectRatio(option: AspectRatio): void {
    this.gfxBridge.setCurrentAspectRatio(option);
    this.resizeAllOpenIframesToCurrentBaseSize();
  }

  getCurrentBaseStageSize(): { w: number; h: number } {
    return this.gfxBridge.getCurrentBaseStageSize();
  }

  /**
   * Resizes all currently open iframes to the base stage size for the current
   * aspect ratio, preserving each segment's per-destination Scale coefficient
   * from `gfxParams["Scale"]` (stored in runtime.params).
   */
  private resizeAllOpenIframesToCurrentBaseSize(): void {
    for (const runtime of this.activeGfxInstances().values()) {
      const scale = this.getIframeScaleFromParams(runtime.params);
      this.gfxBridge.setIframeScale(runtime.timelineGfxId, scale);
    }
  }

  // ── Signal setters (thin wrappers for imperative call sites) ────────

  setEditTimeMs(ms: number): void {
    this.editTimeMs.set(ms);
  }

  setEffectiveGraphics(graphics: ClippingStudioGraphicEvent[]): void {
    this.effectiveGraphics.set(graphics);
    this.syncRuntimeParamsFromEffectiveGraphics(graphics);
  }

  /**
   * Keep iframe scale in sync with the current effective destination state.
   * When the user switches destination (or overrides change), `gfxParams["Scale"]`
   * may change per destination; apply it to the already-open iframe.
   */
  private syncRuntimeParamsFromEffectiveGraphics(
    graphics: ClippingStudioGraphicEvent[],
  ): void {
    const current = this.activeGfxInstances();
    let changed = false;
    const next = new Map(current);

    for (const g of graphics) {
      if (g.type !== "gfx" || g.gfxInstanceId == null) continue;
      const runtime = next.get(g.id);
      if (!runtime) continue;
      const nextParams = g.gfxParams ?? {};
      if (runtime.params === nextParams) continue;

      next.set(g.id, { ...runtime, params: nextParams });
      changed = true;

      const scale = this.getIframeScaleFromParams(nextParams);
      this.gfxBridge.setIframeScale(g.id, scale);
    }

    if (changed) {
      this.activeGfxInstances.set(next);
    }
  }

  /**
   * SQZ classification is a property of the GFX package (backend
   * `gfxInstanceId`), not of a specific timeline segment. It is resolved at
   * Add-flow time from the most recently loaded iframe for that instance.
   *
   * We derive the answer by scanning active runtimes; any segment referencing
   * the same `gfxInstanceId` will have the same SQZ value from the bridge.
   */
  isSqz(gfxInstanceId: number): boolean {
    const instances = this.activeGfxInstances();
    for (const runtime of instances.values()) {
      if (runtime.gfxInstanceId === gfxInstanceId) {
        return this.gfxBridge.isSqz(runtime.timelineGfxId);
      }
    }
    return false;
  }

  /**
   * Seeks each visible GFX iframe to the frame for the current `editTimeMs` (and loaded segments).
   * Intended to run from the preview overlay draw path immediately before `captureSnapshot`, so seek
   * and snapshot use the same timeline moment. The CreateJS ticker stays off after bootstrap; the host drives frames.
   */
  seekVisibleForPreview(): void {
    const visible = this.visibleGfxEntries();
    for (const entry of visible) {
      this.gfxBridge.ensureInitialized(entry.timelineGfxId);
      this.gfxBridge.seekTo(entry.timelineGfxId, entry.localMs);
    }
  }

  // ── Materialize / instance lifecycle (instance-keyed) ───────────────

  materializeInstance(gfxInstanceId: number): Promise<GfxCacheResult> {
    const cached = this.lastMaterializeResult.get(gfxInstanceId);
    if (cached?.isSuccess && cached.status === "ready") {
      return Promise.resolve(cached);
    }

    const existing = this.materializeInFlight.get(gfxInstanceId);
    if (existing) return existing;

    const promise = (async (): Promise<GfxCacheResult> => {
      try {
        const res = await this.gfxRepository.materializeGfx(gfxInstanceId);
        this.lastMaterializeResult.set(gfxInstanceId, res);
        return res;
      } catch (err) {
        const msg = (err as Error)?.message ?? "materialize_failed";
        const res: GfxCacheResult = { isSuccess: false, status: "error", error: "materialize_failed", message: msg };
        this.lastMaterializeResult.set(gfxInstanceId, res);
        return res;
      } finally {
        this.materializeInFlight.delete(gfxInstanceId);
      }
    })();

    this.materializeInFlight.set(gfxInstanceId, promise);
    return promise;
  }

  private async ensureMaterialized(gfxInstanceId: number): Promise<GfxCacheResult> {
    const cached = this.lastMaterializeResult.get(gfxInstanceId);
    if (cached?.isSuccess && cached.status === "ready") return cached;

    const first = await this.materializeInstance(gfxInstanceId);
    if (first.isSuccess && first.status === "ready") return first;
    const second = await this.materializeInstance(gfxInstanceId);
    return second;
  }

  async ensureMaterializedAndGetUrl(
    gfxInstanceId: number,
  ): Promise<{ ok: true; baseUrl: string } | { ok: false; error: string; message?: string }> {
    const mat = await this.ensureMaterialized(gfxInstanceId);
    if (!mat.isSuccess || mat.status !== "ready") {
      return { ok: false, error: mat.error ?? "materialize_failed", message: mat.message ?? undefined };
    }

    try {
      const baseUrl = await this.gfxRepository.getGfxUrl(gfxInstanceId);
      if (baseUrl) {
        return { ok: true, baseUrl };
      }
    } catch {
      // fall through to retry path
    }

    const mat2 = await this.ensureMaterialized(gfxInstanceId);
    if (!mat2.isSuccess || mat2.status !== "ready") {
      return { ok: false, error: mat2.error ?? "materialize_failed", message: mat2.message ?? undefined };
    }
    try {
      const baseUrl2 = await this.gfxRepository.getGfxUrl(gfxInstanceId);
      if (baseUrl2) {
        return { ok: true, baseUrl: baseUrl2 };
      }
      return { ok: false, error: "get_gfx_url_failed" };
    } catch (err) {
      return { ok: false, error: "get_gfx_url_failed", message: (err as Error)?.message ?? undefined };
    }
  }

  private static readonly DEFAULT_ADD_TIMEOUT_MS = 30_000;

  /**
   * Awaitable loader used exclusively by the root add-GFX flow.
   * Two-phase readiness within a single deadline:
   *   Phase A — wait for `gfx-loaded` postMessage (bridge script / window load).
   *   Phase B — poll until `StartAnimation` is assigned (Animate `handleComplete` finished).
   * After both, bootstrap the scene (`StartAnimation` + immediate stop).
   *
   * @param timelineGfxId Pre-generated `ClippingStudioGraphicEvent.id` for the new segment.
   * @param gfxInstanceId Backend GFX package id (used for materialization + base URL).
   *
   * On failure (including timeout) the iframe and runtime entry are torn down
   * so no stale artefacts remain.
   */
  async loadGfxForAddToTimeline(
    timelineGfxId: string,
    gfxInstanceId: number,
    params: Record<string, string>,
    opts?: { timeoutMs?: number },
  ): Promise<void> {
    if (!this.container) {
      throw new Error("GFX container not set");
    }

    const timeoutMs = opts?.timeoutMs ?? ClippingStudioGfxManagerService.DEFAULT_ADD_TIMEOUT_MS;
    const deadline = Date.now() + timeoutMs;

    this.upsertRuntime(timelineGfxId, {
      timelineGfxId,
      gfxInstanceId,
      serveUrl: "",
      status: "materializing",
      params,
      x: 0,
      y: 0,
      scale: 1,
    });

    try {
      const ensured = await this.ensureMaterializedAndGetUrl(gfxInstanceId);

      if (ensured.ok === false) {
        throw new Error(ensured.message ?? ensured.error ?? "Failed to prepare GFX");
      }

      const fullUrl = this.buildServeUrl(ensured.baseUrl, params);
      const iframeScale = this.getIframeScaleFromParams(params);

      this.gfxBridge.destroyIframe(timelineGfxId);
      this.gfxBridge.createIframe(timelineGfxId, fullUrl, this.container!, {
        scale: iframeScale,
      });

      try {
        await this.waitForGfxLoaded(timelineGfxId, deadline);
        await this.gfxBridge.waitForAnimateReady(timelineGfxId, deadline);
      } catch {
        throw new Error("GFX load timeout");
      }

      this.gfxBridge.bootstrapAfterLoad(timelineGfxId);

      this.upsertRuntime(timelineGfxId, {
        timelineGfxId,
        gfxInstanceId,
        serveUrl: fullUrl,
        status: "ready",
        params,
        x: 0,
        y: 0,
        scale: 1,
      });
    } catch (err) {
      this.removeGfxInstance(timelineGfxId);
      throw err;
    }
  }

  syncFromProject(graphics: ClippingStudioGraphicEvent[]): void {
    const gfxEvents = graphics.filter((g) => g.type === "gfx" && g.gfxInstanceId != null);
    const wantedKeys = new Set(gfxEvents.map((g) => g.id));
    const current = this.activeGfxInstances();

    for (const key of current.keys()) {
      if (!wantedKeys.has(key)) {
        this.removeGfxInstance(key);
      }
    }

    for (const g of gfxEvents) {
      const key = g.id;
      if (!current.has(key) && this.container) {
        this.addGfxInstance(key, g.gfxInstanceId!, g.gfxParams ?? {}).catch((err) => {
          console.error("syncFromProject: addGfxInstance failed", {
            timelineGfxId: key,
            gfxInstanceId: g.gfxInstanceId,
            err,
          });
        });
      }
    }
  }

  private static readonly DEFAULT_SYNC_TIMEOUT_MS = 15_000;

  /**
   * Background loader for sync-from-project / project reopen.
   * Same two-phase readiness as `loadGfxForAddToTimeline` (gfx-loaded then
   * StartAnimation poll then bootstrap), but within the shorter 15 s budget
   * and without a modal — errors are logged, not thrown to UI.
   *
   * @param timelineGfxId Timeline segment id (`ClippingStudioGraphicEvent.id`).
   * @param gfxInstanceId Backend GFX package id.
   */
  async addGfxInstance(
    timelineGfxId: string,
    gfxInstanceId: number,
    params: Record<string, string>,
  ): Promise<void> {
    if (!this.container) {
      return;
    }

    const deadline = Date.now() + ClippingStudioGfxManagerService.DEFAULT_SYNC_TIMEOUT_MS;

    this.gfxLoading.set(true);
    this.upsertRuntime(timelineGfxId, {
      timelineGfxId,
      gfxInstanceId,
      serveUrl: "",
      status: "materializing",
      params,
      x: 0,
      y: 0,
      scale: 1,
    });

    try {
      const ensured = await this.ensureMaterializedAndGetUrl(gfxInstanceId);

      if (ensured.ok === false) {
        this.upsertRuntime(timelineGfxId, {
          timelineGfxId,
          gfxInstanceId,
          serveUrl: "",
          status: "error",
          params,
          x: 0,
          y: 0,
          scale: 1,
        });
        return;
      }

      const fullUrl = this.buildServeUrl(ensured.baseUrl, params);
      const iframeScale = this.getIframeScaleFromParams(params);
      this.gfxBridge.createIframe(timelineGfxId, fullUrl, this.container, {
        scale: iframeScale,
      });

      await this.waitForGfxLoaded(timelineGfxId, deadline);
      await this.gfxBridge.waitForAnimateReady(timelineGfxId, deadline);
      this.gfxBridge.bootstrapAfterLoad(timelineGfxId);

      this.upsertRuntime(timelineGfxId, {
        timelineGfxId,
        gfxInstanceId,
        serveUrl: fullUrl,
        status: "ready",
        params,
        x: 0,
        y: 0,
        scale: 1,
      });
    } catch (err) {
      console.error("addGfxInstance failed", { timelineGfxId, gfxInstanceId, err });
      this.upsertRuntime(timelineGfxId, {
        timelineGfxId,
        gfxInstanceId,
        serveUrl: "",
        status: "error",
        params,
        x: 0,
        y: 0,
        scale: 1,
      });
    } finally {
      this.gfxLoading.set(false);
    }
  }

  removeGfxInstance(timelineGfxId: string): void {
    this.gfxBridge.destroyIframe(timelineGfxId);
    const map = new Map(this.activeGfxInstances());
    map.delete(timelineGfxId);
    this.activeGfxInstances.set(map);
  }

  /**
   * Resizes the existing iframe for a timeline GFX segment using the current
   * `Scale` parameter (coefficient, 1.0 = 100%). Intended for live preview
   * while dragging the scale slider, without destroying/recreating the iframe.
   * Also updates the runtime's stored params so a subsequent seek/snapshot
   * uses the latest scale context.
   */
  applyLiveScale(
    timelineGfxId: string,
    gfxParams: Record<string, string>,
  ): void {
    const scale = this.getIframeScaleFromParams(gfxParams);
    this.gfxBridge.setIframeScale(timelineGfxId, scale);
    const runtime = this.activeGfxInstances().get(timelineGfxId);
    if (runtime) {
      this.upsertRuntime(timelineGfxId, { ...runtime, params: gfxParams });
    }
  }

  updateGfxTransform(
    timelineGfxId: string,
    transform: { x?: number; y?: number; scale?: number },
  ): void {
    const runtime = this.activeGfxInstances().get(timelineGfxId);
    if (!runtime) return;
    this.upsertRuntime(timelineGfxId, {
      ...runtime,
      x: transform.x ?? runtime.x,
      y: transform.y ?? runtime.y,
      scale: transform.scale ?? runtime.scale,
    });
  }

  captureSnapshot(timelineGfxId: string): HTMLCanvasElement | null {
    return this.gfxBridge.captureSnapshot(timelineGfxId);
  }

  private handleSessionExpired(timelineGfxId: string): void {
    const existing = this.reopenDebounce.get(timelineGfxId);
    if (existing) clearTimeout(existing);

    this.reopenDebounce.set(
      timelineGfxId,
      setTimeout(async () => {
        this.reopenDebounce.delete(timelineGfxId);
        const runtime = this.activeGfxInstances().get(timelineGfxId);
        if (!runtime || !this.container) {
          return;
        }

        const deadline = Date.now() + ClippingStudioGfxManagerService.DEFAULT_SYNC_TIMEOUT_MS;

        this.gfxBridge.destroyIframe(timelineGfxId);
        try {
          const baseUrl = await this.gfxRepository.getGfxUrl(runtime.gfxInstanceId);
          const fullUrl = this.buildServeUrl(baseUrl, runtime.params);
          const iframeScale = this.getIframeScaleFromParams(runtime.params);
          this.gfxBridge.createIframe(timelineGfxId, fullUrl, this.container, {
            scale: iframeScale,
          });

          await this.waitForGfxLoaded(timelineGfxId, deadline);
          await this.gfxBridge.waitForAnimateReady(timelineGfxId, deadline);
          this.gfxBridge.bootstrapAfterLoad(timelineGfxId);

          this.upsertRuntime(timelineGfxId, { ...runtime, serveUrl: fullUrl, status: "ready" });
        } catch (err) {
          console.error("handleSessionExpired failed", {
            timelineGfxId,
            gfxInstanceId: runtime.gfxInstanceId,
            err,
          });
          this.upsertRuntime(timelineGfxId, { ...runtime, status: "error" });
        }
      }, 1000),
    );
  }

  private upsertRuntime(timelineGfxId: string, runtime: GfxTimelineRuntime): void {
    const map = new Map(this.activeGfxInstances());
    map.set(timelineGfxId, runtime);
    this.activeGfxInstances.set(map);
  }

  private buildServeUrl(baseUrl: string, params: Record<string, string>): string {
    let url = baseUrl;
    for (const [key, value] of Object.entries(params)) {
      if (key.trim().toLowerCase() === "scale") {
        continue;
      }
      const sep = url.includes("?") ? "&" : "?";
      url += `${sep}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    }
    return url;
  }

  /**
   * Phase A: polls until the bridge receives the `gfx-loaded` postMessage
   * from the iframe, or the absolute `deadlineMs` is reached.
   */
  private waitForGfxLoaded(timelineGfxId: string, deadlineMs: number): Promise<void> {
    if (this.gfxBridge.isLoaded(timelineGfxId)) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      const pollInterval = 200;
      const timer = setInterval(() => {
        if (this.gfxBridge.isLoaded(timelineGfxId)) {
          clearInterval(timer);
          resolve();
          return;
        }
        if (Date.now() >= deadlineMs) {
          clearInterval(timer);
          reject(new Error(`GFX segment ${timelineGfxId} load timeout`));
        }
      }, pollInterval);
    });
  }
}
